import { useState, useEffect } from 'react';
import { X, Download, Share, PlusSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usePWAInstall } from '@/hooks/usePWAInstall';

export function InstallPrompt() {
  const { isInstallable, isInstalled, isIOS, isStandalone, promptInstall } = usePWAInstall();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user has previously dismissed the prompt
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    const dismissedAt = wasDismissed ? parseInt(wasDismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);

    // Show prompt again after 7 days
    if (daysSinceDismissed > 7) {
      setDismissed(false);
    } else if (wasDismissed) {
      setDismissed(true);
    }

    // Show prompt if installable and not dismissed
    if ((isInstallable || isIOS) && !isInstalled && !isStandalone && !dismissed) {
      // Delay showing the prompt for a better UX
      const timer = setTimeout(() => setShowPrompt(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isInstallable, isIOS, isInstalled, isStandalone, dismissed]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    const success = await promptInstall();
    if (success) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt || isInstalled || isStandalone) return null;

  return (
    <div className="fixed z-50 animate-fade-in-up left-4 right-4 md:left-auto md:right-4 md:max-w-sm" style={{ bottom: 'max(1rem, env(safe-area-inset-bottom))' }}>
      <div className="rounded-xl border border-border bg-card p-4 shadow-lg backdrop-blur-sm ml-[env(safe-area-inset-left)] mr-[env(safe-area-inset-right)]">
        <button
          onClick={handleDismiss}
          className="absolute right-2 top-2 rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="flex items-start gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-accent">
            <Download className="h-6 w-6 text-white" />
          </div>

          <div className="flex-1 pr-4">
            <h3 className="font-semibold text-foreground">Install VCM App</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {isIOS
                ? 'Add to your home screen for the best experience'
                : 'Install for quick access and offline support'}
            </p>
          </div>
        </div>

        {isIOS ? (
          <div className="mt-4 rounded-lg bg-muted/50 p-3">
            <p className="text-sm text-muted-foreground">
              <span className="flex items-center gap-2">
                <span>1. Tap</span>
                <Share className="h-4 w-4" />
                <span>Share</span>
              </span>
              <span className="mt-1 flex items-center gap-2">
                <span>2. Tap</span>
                <PlusSquare className="h-4 w-4" />
                <span>Add to Home Screen</span>
              </span>
            </p>
          </div>
        ) : (
          <div className="mt-4 flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="flex-1"
            >
              Not now
            </Button>
            <Button
              size="sm"
              onClick={handleInstall}
              className="flex-1 bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90"
            >
              Install
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
